// Franja de oferta: la cuenta atrás y el apagado automático.
//
// La franja llega del servidor con `hidden` puesto. Se enciende aquí, ya con
// el reloj calculado, para que nunca se vea un instante de «Termina en» sin
// número ni una franja de una oferta que terminó ayer.

const UN_SEGUNDO = 1000;
const UN_MINUTO = 60 * UN_SEGUNDO;
const UN_DIA = 24 * 60 * UN_MINUTO;

// La tienda es dominicana y despacha a Estados Unidos y Puerto Rico. Si la
// fecha se leyera en la hora de quien mira, «termina a las 23:59» ocurriría a
// una hora distinta en Miami que en Santo Domingo. Se fija el huso: -04:00.
const HUSO_RD = '-04:00';

// Lo escribe una persona en una casilla de texto, así que esto no puede
// romperse por un espacio de más ni por que falte la hora. Lo que no se
// entienda devuelve null y la franja sale sin reloj, nunca rota.
function leerFecha(texto) {
  const [dia, hora = ''] = String(texto || '').trim().split(/[T\s]+/);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia || '')) return null;

  // Sin hora, la oferta termina al final de ese día. El `|| '23:59'` va antes
  // de partir por los dos puntos a propósito: partir una cadena vacía devuelve
  // [''], que no es undefined y por tanto no dispara el valor por defecto.
  const [hh = '23', mm = '00'] = (hora || '23:59').split(':');
  const fecha = new Date(`${dia}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00${HUSO_RD}`);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

// Dos cifras siempre: «02h» y no «2h». Con ancho fijo el número no baila al
// pasar de 10 a 9, que es lo que hace que una cuenta atrás se lea como
// urgencia y no como parpadeo.
const dos = (n) => String(Math.floor(n)).padStart(2, '0');

function comoTexto(faltan) {
  const dias = Math.floor(faltan / UN_DIA);
  const horas = Math.floor((faltan % UN_DIA) / (60 * UN_MINUTO));
  const minutos = Math.floor((faltan % (60 * UN_MINUTO)) / UN_MINUTO);
  const segundos = Math.floor((faltan % UN_MINUTO) / UN_SEGUNDO);

  // Con más de un día por delante los segundos son ruido: nadie compra por
  // ver bajar un segundo cuando quedan tres días. En el último día sí, y ahí
  // es justo donde aprietan.
  if (dias > 0) return `${dos(dias)}d ${dos(horas)}h ${dos(minutos)}m`;
  return `${dos(horas)}h ${dos(minutos)}m ${dos(segundos)}s`;
}

class FranjaDeOferta extends HTMLElement {
  connectedCallback() {
    this.hasta = leerFecha(this.dataset.hasta);
    this.reloj = this.querySelector('[data-reloj]');
    this.tiempo = this.querySelector('[data-tiempo]');

    // Sin fecha, la franja es fija y no hay nada que contar.
    if (!this.hasta) return void this.encender();

    if (!this.pintar()) return;       // ya terminó: se queda escondida
    this.encender();
    this.reloj?.removeAttribute('hidden');

    // Cada segundo mientras queda menos de un día; cada medio minuto cuando
    // aún quedan días, que es tiempo de sobra y no hace falta gastar batería.
    const faltan = this.hasta - Date.now();
    this.latido = setInterval(() => {
      if (!this.pintar()) this.apagar();
    }, faltan > UN_DIA ? 30 * UN_SEGUNDO : UN_SEGUNDO);
  }

  disconnectedCallback() {
    clearInterval(this.latido);
    this.observador?.disconnect();
  }

  /** Escribe el tiempo que queda. Devuelve false si la oferta ya terminó. */
  pintar() {
    const faltan = this.hasta - Date.now();
    if (faltan <= 0) return false;
    if (this.tiempo) this.tiempo.textContent = comoTexto(faltan);
    return true;
  }

  encender() {
    this.removeAttribute('hidden');
    this.acomodarConElHeader();
    this.corregirElNumero();
  }

  /**
   * Corrige el porcentaje con la rebaja real de la colección.
   *
   * El número lo escribe una persona en Personalizar y envejece: decía «hasta
   * −50%» mientras había prendas al 80%. Eso promete menos de lo que hay y
   * encima deja de ser cierto en cuanto alguien toca un precio.
   *
   * Sólo corrige **hacia arriba**: si el servidor no contesta, o devuelve algo
   * menor, se queda lo escrito. Así un fallo aquí nunca rebaja la promesa por
   * su cuenta, y lo que se ve de entrada ya es correcto —no hay parpadeo de un
   * número cambiando delante de quien mira.
   */
  async corregirElNumero() {
    const servidor = this.dataset.servidor;
    const nodo = this.querySelector('.sc-saleband__figure');
    if (!servidor || !nodo) return;

    try {
      const res = await fetch(`${servidor}/tienda/api/oferta`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const { maximo } = await res.json();
      if (!Number.isFinite(maximo) || maximo <= 0) return;

      // Se respeta lo que la persona escribió alrededor del número —«HASTA
      // −50%» sigue siendo «HASTA −80%»— y sólo se cambia la cifra.
      const escrito = Number((nodo.textContent.match(/(\d+)\s*%/) || [])[1]);
      if (Number.isFinite(escrito) && escrito >= maximo) return;
      nodo.textContent = nodo.textContent.replace(/\d+\s*%/, `${maximo}%`);
    } catch {
      /* sin conexión se queda lo escrito, que siempre es válido */
    }
  }

  apagar() {
    clearInterval(this.latido);
    this.setAttribute('hidden', '');
    document.documentElement.style.removeProperty('--sc-saleband-h');
  }

  /**
   * En la portada el header va superpuesto y fijo, así que no deja sitio a
   * nadie: una franja en el flujo normal se quedaría debajo de él, tapada.
   * Ahí la franja también se fija, y el header baja lo que ella mide.
   *
   * La altura se mide en vez de escribirse a mano porque cambia con el ancho
   * de la pantalla y con lo largo que sea el texto que Laura escriba.
   */
  acomodarConElHeader() {
    const superpuesto = document.querySelector('sc-header.sc-header--overlay');
    if (superpuesto) this.dataset.superpuesto = '';

    const medir = () => {
      const alto = this.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--sc-saleband-h', `${Math.round(alto)}px`);
    };
    medir();

    // Al girar el teléfono o cambiar el ancho, la franja puede pasar a dos
    // líneas y el header tiene que enterarse.
    if ('ResizeObserver' in window) {
      this.observador = new ResizeObserver(medir);
      this.observador.observe(this);
    }
  }
}

if (!customElements.get('sc-sale-banner')) {
  customElements.define('sc-sale-banner', FranjaDeOferta);
}
